/**
 * Shared formatting utilities and stage configuration.
 *
 * Single source of truth for:
 * - Currency formatting (replaces duplicated formatCurrency across pages)
 * - Chart color palette
 * - Pipeline stage definitions (matching fleet-admin's VehiclesRecords.tsx)
 * - Stage badge styles (matching fleet-admin's exact theming)
 * - Business-timezone dates, weeks and money-query bounds (mirrors fleet-admin's lib/format.ts)
 */

import { addDays, isValid, parse, startOfWeek } from 'date-fns';

// ─── Currency ────────────────────────────────────────────────────────────────

export function formatCurrency(amount: number) {
    return 'GHS' + new Intl.NumberFormat('en', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

export function formatCompactCurrency(amount: number) {
    if (amount >= 1_000_000) return `GHS${(amount / 1_000_000).toFixed(1)}M`;
    if (amount >= 1_000) return `GHS${(amount / 1_000).toFixed(0)}k`;
    return formatCurrency(amount);
}

// ─── Chart Colors ────────────────────────────────────────────────────────────

export const CHART_COLORS = {
    slate900: '#0f172a',
    slate600: '#475569',
    slate400: '#94a3b8',
    slate300: '#cbd5e1',
    emerald500: '#10b981',
    amber500: '#f59e0b',
    red500: '#ef4444',
    blue500: '#3b82f6',
    cyan500: '#06b6d4',
    indigo500: '#6366f1',
};

// ─── Pipeline Stage Definitions ──────────────────────────────────────────────

// Partner-simplified procurement stages (subset of admin's 8-stage pipeline)
export type ProcurementStage = 'reserved' | 'shipped' | 'arrived' | 'cleared' | 'ready';
export type ContractStage = 'draft' | 'active' | 'completed' | 'terminated' | 'sold';
export type VehicleStage = ProcurementStage | ContractStage;

export interface PipelineStage {
    key: string;
    label: string;
    count: number;
    color: string;
}

// Stage configs — matching fleet-admin's color names for consistency
export const PROCUREMENT_STAGE_CONFIG: { key: ProcurementStage; label: string; color: string }[] = [
    { key: 'reserved', label: 'Reserved', color: 'slate' },
    { key: 'shipped', label: 'Shipped', color: 'cyan' },
    { key: 'arrived', label: 'Arrived', color: 'indigo' },
    { key: 'cleared', label: 'Cleared', color: 'blue' },
    { key: 'ready', label: 'Ready', color: 'green' },
];

export const CONTRACT_STAGE_CONFIG: { key: ContractStage; label: string; color: string }[] = [
    { key: 'draft', label: 'Draft', color: 'amber' },
    { key: 'active', label: 'Active', color: 'emerald' },
    { key: 'completed', label: 'Completed', color: 'slate' },
    { key: 'terminated', label: 'Terminated', color: 'red' },
    { key: 'sold', label: 'Sold', color: 'slate' },
];

// ─── Stage Badge Styles ──────────────────────────────────────────────────────
// Exact match of fleet-admin's STAGE_BADGE_STYLES from VehiclesRecords.tsx

export const STAGE_BADGE_STYLES: Record<VehicleStage, string> = {
    // Procurement stages
    reserved: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400',
    shipped: 'border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-400',
    arrived: 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-400',
    cleared: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-400',
    ready: 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-400',
    // Contract stages
    draft: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400',
    active: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400',
    completed: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400',
    terminated: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400',
    sold: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400',
};

export const STAGE_LABELS: Record<VehicleStage, string> = {
    reserved: 'Reserved',
    shipped: 'Shipped',
    arrived: 'Arrived',
    cleared: 'Cleared',
    ready: 'Ready',
    draft: 'Draft',
    active: 'Active',
    completed: 'Completed',
    terminated: 'Terminated',
    sold: 'Sold',
};

// ─── Business Timezone ───────────────────────────────────────────────────────

// The payout and earnings RPCs bucket rows by the Ghana calendar day of `transaction_at` /
// `payment_date`, so labels and default ranges must be derived in the same zone, or the display
// contradicts the filter.
export const BUSINESS_TZ = 'Africa/Accra'; // UTC+0 year-round, no DST

const BUSINESS_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TZ,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
});

const BUSINESS_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
});

const BUSINESS_DAY_PARTS_FORMATTER = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
});

/** Intl throws RangeError on an Invalid Date, so unusable input is filtered out here. */
function toValidDate(value: string | Date | null | undefined): Date | null {
    if (value === null || value === undefined) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

/** 'Aug 9, 2026' in the business timezone; '-' when the value is missing or unparseable. */
export function formatBusinessDate(value: string | Date | null | undefined): string {
    const date = toValidDate(value);
    return date ? BUSINESS_DATE_FORMATTER.format(date) : '-';
}

/** '11:32 PM' in the business timezone; '-' when the value is missing or unparseable. */
export function formatBusinessTime(value: string | Date | null | undefined): string {
    const date = toValidDate(value);
    return date ? BUSINESS_TIME_FORMATTER.format(date) : '-';
}

/**
 * The business-timezone calendar day of an instant, as a Date at *local* midnight — so any date-fns
 * pattern (`format(businessDay(x)!, 'MMM d')`) renders the business day rather than the viewer's.
 * Null when the value is missing or unparseable.
 */
export function businessDay(value: string | Date | null | undefined): Date | null {
    const date = toValidDate(value);
    if (!date) return null;
    const parts = BUSINESS_DAY_PARTS_FORMATTER.formatToParts(date);
    const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(p => p.type === type)?.value);
    return new Date(part('year'), part('month') - 1, part('day'), 0, 0, 0, 0);
}

/**
 * Today's business-timezone calendar date as a Date at *local* midnight, so date-fns helpers
 * (startOfMonth/startOfWeek/format(…, 'yyyy-MM-dd')) keep serialising the business day.
 */
export function businessToday(): Date {
    return businessDay(new Date())!;
}

// ─── Business Week & Query Bounds ────────────────────────────────────────────

/*
 * Money queries are filtered by `transaction_at`, an instant, but attributed to a Ghana calendar
 * day — so the *bounds* have to be Ghana instants built from calendar parts. `toISOString()` on a
 * local Date is the bug this section exists to remove: from BST it serialises Monday 00:00 as
 * '…T23:00:00.000Z' the Sunday before, pulling the previous week's last hour of payments into this
 * week's payout and dropping this week's first hour out of it.
 */

/**
 * A calendar-day Date rendered from its *local* parts. Calendar-day Dates — `businessDay()` and
 * `businessToday()` output, a date-picker selection, any date-fns value derived from one — carry
 * the day they stand for in those parts, so this reads the day the viewer is actually looking at.
 */
function toYmd(day: Date): string {
    const year = String(day.getFullYear()).padStart(4, '0');
    const month = String(day.getMonth() + 1).padStart(2, '0');
    const date = String(day.getDate()).padStart(2, '0');
    return `${year}-${month}-${date}`;
}

/**
 * A 'yyyy-MM-dd' calendar string — a `date` column, a `?week=` URL param — as a calendar-day Date.
 * Parsed field by field: `new Date('2026-08-10')` would read it as UTC midnight, which is the
 * previous day west of Greenwich. Null when the value is missing or unparseable.
 */
export function parseBusinessDay(value: string | null | undefined): Date | null {
    if (!value) return null;
    const parsed = parse(value.substring(0, 10), 'yyyy-MM-dd', new Date());
    return isValid(parsed) ? parsed : null;
}

/** Inclusive lower bound for a `transaction_at` filter: the instant `day` opens in Ghana. */
export function businessDayStartInstant(day: Date): string {
    return `${toYmd(day)}T00:00:00.000Z`;
}

/**
 * Inclusive upper bound for a `transaction_at` filter: the last instant of `day` in Ghana.
 *
 * Microseconds, not milliseconds. Postgres holds `timestamptz` to microsecond precision and
 * hundreds of production `transactions` rows carry a sub-millisecond `transaction_at`; against a
 * '…T23:59:59.999Z' bound each of those compares greater and silently drops out of the week whose
 * payout it belongs to.
 */
export function businessDayEndInstant(day: Date): string {
    return `${toYmd(day)}T23:59:59.999999Z`;
}

export interface BusinessWeekBounds {
    /** Monday of the week, as a calendar-day Date — safe to hand to date-fns `format`. */
    weekStart: Date;
    /** Sunday of the week, as a calendar-day Date. */
    weekEnd: Date;
    /** The Monday as 'yyyy-MM-dd' — the week's identity in URLs, query keys and row keys. */
    weekStartYmd: string;
    /** Inclusive `transaction_at` lower bound: Monday 00:00:00.000 Ghana. */
    fromInstant: string;
    /** Inclusive `transaction_at` upper bound: Sunday 23:59:59.999999 Ghana. */
    toInstant: string;
}

/**
 * A Date argument as the calendar day it stands for.
 *
 * THE ARGUMENT IS CONTRACTUALLY A CALENDAR-DAY DATE — `businessDay()` or `businessToday()` output,
 * a date-picker selection, any date-fns value derived from one. That shape is local midnight and
 * already carries its day in its local parts, so it passes through untouched. Normalising it
 * through `businessDay()` would be wrong, not merely redundant: local midnight in Kiritimati
 * (UTC+14) is 10:00 the PREVIOUS day in Accra, so a picker selection would be walked back a day.
 *
 * A value carrying a clock is an *instant* that came through the wrong door, and is salvaged to its
 * Accra day rather than read as the viewer's. That salvage is BEST EFFORT and cannot be completed:
 * an instant landing exactly on the viewer's midnight is byte-identical to a picker's selection of
 * the next day, so no heuristic can separate them. That is why instants are outside
 * `businessWeekBounds`' contract and `businessWeekBoundsOfInstant` exists for values that are ones.
 */
function toCalendarDay(reference: Date): Date {
    const isCalendarDay = reference.getHours() === 0
        && reference.getMinutes() === 0
        && reference.getSeconds() === 0
        && reference.getMilliseconds() === 0;
    // `businessDay` is null only for an Invalid Date, which falls back to the current week exactly
    // as an unparseable string does, rather than serialising 'NaN-NaN-NaN' into a query.
    return isCalendarDay ? reference : (businessDay(reference) ?? businessToday());
}

/**
 * The Ghana business week (Monday–Sunday) containing `reference`, with the bounds a money query
 * needs. An absent, unparseable or invalid reference falls back to the current business week.
 *
 * `reference` is EITHER a 'yyyy-MM-dd' calendar string — a `date` column, a `?week=` URL param, a
 * full timestamp string read for its day — OR a CALENDAR-DAY Date: `businessToday()`, a picker
 * selection, a date-fns result. A RAW INSTANT Date IS NOT SUPPORTED. `new Date(transaction_at)`
 * that happens to fall on the viewer's local midnight — 2026-08-09T23:00:00Z in London, that same
 * Ghana Sunday as 2026-08-09T10:00:00Z in Kiritimati — is the identical value a picker produces for
 * the Monday after, so one call would have to answer two weeks. Route instants through
 * `businessWeekBoundsOfInstant` below (or resolve them yourself with `businessDay`) instead.
 */
export function businessWeekBounds(reference: Date | string | null | undefined): BusinessWeekBounds {
    const day = reference instanceof Date
        ? toCalendarDay(reference)
        : (parseBusinessDay(reference) ?? businessToday());
    const weekStart = startOfWeek(day, { weekStartsOn: 1 });
    const weekEnd = addDays(weekStart, 6);
    return {
        weekStart,
        weekEnd,
        weekStartYmd: toYmd(weekStart),
        fromInstant: businessDayStartInstant(weekStart),
        toInstant: businessDayEndInstant(weekEnd),
    };
}

/**
 * The Ghana business week containing an INSTANT — a `transaction_at`, a `created_at`, any
 * `timestamptz` — for callers holding a moment in time rather than a day.
 *
 * Resolving the Accra day FIRST is what makes this unambiguous where `businessWeekBounds`' Date
 * argument cannot be: `businessDay` reads the instant in Africa/Accra whatever the viewer's clock
 * says, so a Sunday 23:00Z payment stays in its own week from London and from Kiritimati alike —
 * including the local-midnight instants the Date branch has to read as calendar days.
 */
export function businessWeekBoundsOfInstant(value: string | Date): BusinessWeekBounds {
    const day = businessDay(value);
    // Handed back as a calendar string, the branch with no shape left to infer. An unparseable
    // value falls through to the current business week exactly as it does above.
    return businessWeekBounds(day ? toYmd(day) : null);
}

